import { useState, useEffect } from 'react';
import { Users, Building, CreditCard, Bell, Shield, Globe, UserPlus, Mail, MessageSquare, Bot } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { InviteUserModal } from './InviteUserModal';
import { WorkspaceMember, WorkspaceInvite, WorkspaceSettings } from '../../types';
import { WorkspaceSetup } from './WorkspaceSetup';
import { EmailSettings } from './EmailSettings';
import { TeamChat } from '../chat/TeamChat';
import { GeminiChat } from '../ai/GeminiChat';
import { WorkspaceManagement } from './WorkspaceManagement';
import {
  getWorkspaceMembers,
  getWorkspaceInvites,
  updateWorkspaceMemberRole,
  removeWorkspaceMember,
  cancelWorkspaceInvite,
  subscribeToWorkspaceMembers,
  subscribeToWorkspaceInvites,
  inviteWorkspaceMember,
  updateWorkspaceSettings
} from '../../services/firestore';
import { emailService } from '../../services/emailService';

export const SUPPORTED_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QAR' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'TND' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'DZD' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'L£' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'JD' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KD' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR' },
];

const DEFAULT_BRANDING = {
  companyName: 'Your Company Name',
  address: '123 Business St\nDubai, UAE',
  email: 'contact@company.com',
  phone: '+1 (555) 123-4567',
  mobile: '+971 559670015',
  licenseNumber: '1394856',
  primaryColor: '#22C55E',
};

const defaultSettings: WorkspaceSettings = {
    currency: 'USD',
    taxRate: 0,
    invoicePrefix: 'INV',
    paymentMethods: [],
    branding: {
    ...DEFAULT_BRANDING
  },
  emailProvider: 'resend',
};

export function Settings() {
  const { currentWorkspace, user } = useAuth();
  const [activeTab, setActiveTab] = useState('business');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add local state for branding and payment settings
  const blankBranding = {
    primaryColor: '',
    logo: '',
    companyName: '',
    address: '',
    email: '',
    phone: '',
    mobile: '',
    website: '',
    taxId: '',
    licenseNumber: '',
    city: '',
    postalCode: ''
  };
  const [brandingState, setBrandingState] = useState({
    ...blankBranding,
    ...(currentWorkspace?.settings?.branding || {})
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [paymentState, setPaymentState] = useState({
    defaultPaymentGateway: currentWorkspace?.settings?.defaultPaymentGateway || 'custom',
    stripePublicKey: currentWorkspace?.settings?.stripePublicKey || '',
    stripeSecretKey: currentWorkspace?.settings?.stripeSecretKey || '',
    paypalClientId: currentWorkspace?.settings?.paypalClientId || '',
    paypalSecret: currentWorkspace?.settings?.paypalSecret || '',
    paypalEnvironment: currentWorkspace?.settings?.paypalEnvironment || 'sandbox',
    razorpayKeyId: currentWorkspace?.settings?.razorpayKeyId || '',
    razorpayKeySecret: currentWorkspace?.settings?.razorpayKeySecret || '',
    tapApiKey: currentWorkspace?.settings?.tapApiKey || '',
    paytmMerchantId: currentWorkspace?.settings?.paytmMerchantId || '',
    paytmMerchantKey: currentWorkspace?.settings?.paytmMerchantKey || '',
    paytmEnvironment: currentWorkspace?.settings?.paytmEnvironment || 'test',
  });

  // Sync local state with workspace changes
  useEffect(() => {
    setBrandingState({
      ...blankBranding,
      ...(currentWorkspace?.settings?.branding || {})
    });
    setPaymentState({
      defaultPaymentGateway: currentWorkspace?.settings?.defaultPaymentGateway || 'custom',
      stripePublicKey: currentWorkspace?.settings?.stripePublicKey || '',
      stripeSecretKey: currentWorkspace?.settings?.stripeSecretKey || '',
      paypalClientId: currentWorkspace?.settings?.paypalClientId || '',
      paypalSecret: currentWorkspace?.settings?.paypalSecret || '',
      paypalEnvironment: currentWorkspace?.settings?.paypalEnvironment || 'sandbox',
      razorpayKeyId: currentWorkspace?.settings?.razorpayKeyId || '',
      razorpayKeySecret: currentWorkspace?.settings?.razorpayKeySecret || '',
      tapApiKey: currentWorkspace?.settings?.tapApiKey || '',
      paytmMerchantId: currentWorkspace?.settings?.paytmMerchantId || '',
      paytmMerchantKey: currentWorkspace?.settings?.paytmMerchantKey || '',
      paytmEnvironment: currentWorkspace?.settings?.paytmEnvironment || 'test',
    });
  }, [currentWorkspace]);

  useEffect(() => {
    if (!currentWorkspace) return;

    // Load initial data
    const loadData = async () => {
      try {
        const [workspaceMembers, workspaceInvites] = await Promise.all([
          getWorkspaceMembers(currentWorkspace.id),
          getWorkspaceInvites(currentWorkspace.id)
        ]);
        setMembers(workspaceMembers);
        setInvites(workspaceInvites);
      } catch (error) {
        console.error('Error loading workspace members:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();

    // Set up real-time listeners
    const unsubscribeMembers = subscribeToWorkspaceMembers(currentWorkspace.id, (updatedMembers) => {
      setMembers(updatedMembers);
    });

    const unsubscribeInvites = subscribeToWorkspaceInvites(currentWorkspace.id, (updatedInvites) => {
      setInvites(updatedInvites);
    });

    return () => {
      unsubscribeMembers();
      unsubscribeInvites();
    };
  }, [currentWorkspace]);

  const tabs = [
    { id: 'business', name: 'Business Info', icon: Building },
    { id: 'workspaces', name: 'Workspaces', icon: Building },
    { id: 'users', name: 'Users & Teams', icon: Users },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'integrations', name: 'Integrations', icon: Globe },
  ];

  const handleInviteUser = async (email: string, role: 'admin' | 'member') => {
    if (!currentWorkspace) return;

    try {
      await inviteWorkspaceMember(currentWorkspace.id, email, role);
      // The real-time listener will update the invites list
    } catch (error) {
      console.error('Error inviting user:', error);
      alert('Failed to send invitation. Please try again.');
    }
  };

  const handleUpdateRole = async (memberId: string, role: WorkspaceMember['role']) => {
    try {
      await updateWorkspaceMemberRole(memberId, role);
      // The real-time listener will update the members list
    } catch (error) {
      console.error('Error updating member role:', error);
      alert('Failed to update role. Please try again.');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await removeWorkspaceMember(memberId);
      // The real-time listener will update the members list
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelWorkspaceInvite(inviteId);
      // The real-time listener will update the invites list
    } catch (error) {
      console.error('Error canceling invite:', error);
      alert('Failed to cancel invitation. Please try again.');
    }
  };

  const renderUsersTeams = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </button>
      </div>
      
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading team members...</p>
        </div>
      ) : (
        <>
          {/* Active Members */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{member.email}</div>
                          {member.userId === currentWorkspace?.ownerId && (
                            <span className="text-xs text-blue-600 font-medium">Workspace Owner</span>
                          )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                      {member.userId === currentWorkspace?.ownerId ? (
                        <span className="text-sm text-gray-900">Owner</span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.id, e.target.value as WorkspaceMember['role'])}
                          className="text-sm border-gray-300 rounded-md"
                          disabled={member.userId === user?.id}
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {member.userId !== currentWorkspace?.ownerId && member.userId !== user?.id && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pending Invites */}
          {invites.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Pending Invitations</h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sent
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invites.map((invite) => (
                      <tr key={invite.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invite.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {invite.role}
              </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invite.createdAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleCancelInvite(invite.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Cancel
                          </button>
              </td>
            </tr>
                    ))}
          </tbody>
        </table>
      </div>
            </div>
          )}
        </>
      )}

      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
      />
    </div>
  );

  // Branding change handler
  const handleBrandingChange = (field: string, value: string) => {
    setBrandingState(prev => ({ ...prev, [field]: value }));
  };

  // Logo upload handler (just updates local state)
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setBrandingState(prev => ({ ...prev, logo: event.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Save branding
  const handleSaveBranding = () => {
    handleUpdateWorkspaceSettings({ branding: brandingState });
  };

  // Payment change handler
  const handlePaymentChange = (field: string, value: string) => {
    setPaymentState(prev => ({ ...prev, [field]: value }));
  };

  // Save payment settings
  const handleSavePayment = () => {
    handleUpdateWorkspaceSettings(paymentState);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'business':
        return <WorkspaceSetup />;
      case 'workspaces':
        return <WorkspaceManagement />;
      case 'users':
        return renderUsersTeams();
      case 'payments':
        return renderPaymentSettings();
      case 'email':
        return renderEmailSettings();
      case 'notifications':
        return <div>Notifications settings coming soon...</div>;
      case 'security':
        return <div>Security settings coming soon...</div>;
      case 'integrations':
        return <div>Integrations settings coming soon...</div>;
      default:
        return null;
    }
  };

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Payment Settings</h3>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        {/* Default Payment Gateway */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Default Payment Gateway</h4>
          <div className="space-y-4">
            <select
              value={paymentState.defaultPaymentGateway}
              onChange={(e) => handlePaymentChange('defaultPaymentGateway', e.target.value as 'custom' | 'stripe' | 'paypal' | 'razorpay' | 'tap' | 'paytm')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="custom">Custom Payment Link</option>
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="razorpay">Razorpay</option>
              <option value="tap">Tap Payments</option>
              <option value="paytm">Paytm</option>
            </select>
            <p className="text-sm text-gray-500">
              Select your preferred payment gateway for accepting payments from clients
            </p>
          </div>
        </div>

        {/* Gateway Specific Settings */}
        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-4">Gateway Configuration</h4>
          
          {paymentState.defaultPaymentGateway === 'stripe' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stripe Public Key
                </label>
                <input
                  type="text"
                  value={paymentState.stripePublicKey}
                  onChange={(e) => handlePaymentChange('stripePublicKey', e.target.value)}
                  placeholder="pk_test_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stripe Secret Key
                </label>
                <input
                  type="password"
                  value={paymentState.stripeSecretKey}
                  onChange={(e) => handlePaymentChange('stripeSecretKey', e.target.value)}
                  placeholder="sk_test_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}

          {paymentState.defaultPaymentGateway === 'paypal' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PayPal Client ID
                </label>
                <input
                  type="text"
                  value={paymentState.paypalClientId}
                  onChange={(e) => handlePaymentChange('paypalClientId', e.target.value)}
                  placeholder="Client ID from PayPal Developer Dashboard"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PayPal Secret
                </label>
                <input
                  type="password"
                  value={paymentState.paypalSecret}
                  onChange={(e) => handlePaymentChange('paypalSecret', e.target.value)}
                  placeholder="Secret from PayPal Developer Dashboard"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PayPal Environment
                </label>
                <select
                  value={paymentState.paypalEnvironment}
                  onChange={(e) => handlePaymentChange('paypalEnvironment', e.target.value as 'sandbox' | 'production')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="sandbox">Sandbox (Testing)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          )}

          {paymentState.defaultPaymentGateway === 'razorpay' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razorpay Key ID
                </label>
                <input
                  type="text"
                  value={paymentState.razorpayKeyId}
                  onChange={(e) => handlePaymentChange('razorpayKeyId', e.target.value)}
                  placeholder="rzp_test_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razorpay Key Secret
                </label>
                <input
                  type="password"
                  value={paymentState.razorpayKeySecret}
                  onChange={(e) => handlePaymentChange('razorpayKeySecret', e.target.value)}
                  placeholder="Enter your Razorpay Key Secret"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}

          {paymentState.defaultPaymentGateway === 'tap' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tap Payments API Key
                </label>
                <input
                  type="text"
                  value={paymentState.tapApiKey}
                  onChange={(e) => handlePaymentChange('tapApiKey', e.target.value)}
                  placeholder="sk_test_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}

          {paymentState.defaultPaymentGateway === 'paytm' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paytm Merchant ID
                </label>
                <input
                  type="text"
                  value={paymentState.paytmMerchantId}
                  onChange={(e) => handlePaymentChange('paytmMerchantId', e.target.value)}
                  placeholder="Enter your Paytm Merchant ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paytm Merchant Key
                </label>
                <input
                  type="password"
                  value={paymentState.paytmMerchantKey}
                  onChange={(e) => handlePaymentChange('paytmMerchantKey', e.target.value)}
                  placeholder="Enter your Paytm Merchant Key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paytm Environment
                </label>
                <select
                  value={paymentState.paytmEnvironment}
                  onChange={(e) => handlePaymentChange('paytmEnvironment', e.target.value as 'test' | 'production')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="test">Test</option>
                  <option value="production">Production</option>
                </select>
              </div>
            </div>
          )}

          {paymentState.defaultPaymentGateway === 'custom' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                With Custom Payment Link selected, you can add payment links manually when creating invoices.
                This is useful if you want to use a different payment system or handle payments outside of the platform.
              </p>
            </div>
          )}
        </div>

        {/* Test Payment Button */}
        {paymentState.defaultPaymentGateway !== 'custom' && (
          <div className="pt-6 border-t border-gray-200">
            <button
              onClick={handleTestPayment}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Test Payment Integration
            </button>
            <p className="mt-2 text-sm text-gray-500">
              Test your payment gateway integration with a small transaction
            </p>
          </div>
        )}
        <div className="pt-6 border-t border-gray-200">
          <button
            onClick={handleSavePayment}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Payment Settings
          </button>
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="max-w-4xl mx-auto">
      <EmailSettings 
        settings={currentWorkspace?.settings || defaultSettings} 
        onUpdate={handleUpdateWorkspaceSettings} 
        onTestEmail={handleTestEmail}
      />
    </div>
  );

  const renderTeamChat = () => (
    <div className="max-w-4xl mx-auto h-[600px]">
      <TeamChat />
          </div>
        );

  const renderAIAssistant = () => (
    <div className="max-w-4xl mx-auto h-[600px]">
      <GeminiChat />
          </div>
        );

  const handleUpdateWorkspaceSettings = async (settings: Partial<WorkspaceSettings>) => {
    if (!currentWorkspace) return;

    try {
      // Update workspace settings in Firestore
      await updateWorkspaceSettings(currentWorkspace.id, settings);
    } catch (error) {
      console.error('Error updating workspace settings:', error);
      alert('Failed to update settings. Please try again.');
    }
  };

  const handleTestPayment = async () => {
    if (!currentWorkspace?.settings?.defaultPaymentGateway) return;

    try {
      // Create a test payment based on the selected gateway
      const amount = 1; // Minimal amount for testing
      const currency = currentWorkspace.settings.currency || 'USD';
      
      switch (currentWorkspace.settings.defaultPaymentGateway) {
        case 'stripe':
          // Implement Stripe test payment
          break;
        case 'paypal':
          // Implement PayPal test payment
          break;
        case 'razorpay':
          // Implement Razorpay test payment
          break;
        case 'tap':
          // Implement Tap Payments test payment
          break;
        case 'paytm':
          // Implement Paytm test payment
          break;
      }

      alert('Test payment completed successfully!');
    } catch (error) {
      console.error('Error testing payment:', error);
      alert('Failed to test payment. Please check your settings and try again.');
    }
  };

  const handleTestEmail = async () => {
    if (!currentWorkspace?.settings) return;

    const settings = currentWorkspace.settings;
    
    // Only proceed if we have the required values based on provider
    if (settings.emailProvider === 'resend' && !settings.resendApiKey) {
      alert('Please enter your Resend API key');
      return;
    }

    if (settings.emailProvider === 'smtp' && (!settings.smtpHost || !settings.smtpUsername || !settings.smtpPassword)) {
      alert('Please fill in all SMTP settings');
      return;
    }

    try {
      // Use the improved email service with workspace context
      const success = await emailService.testEmailConfiguration(currentWorkspace);
      if (success) {
        alert('Test email sent successfully! Check your inbox.');
      } else {
        alert('Failed to send test email. Please check your configuration and try again.');
      }
    } catch (error) {
      console.error('Error testing email configuration:', error);
      alert('Failed to send test email. Please check your configuration and try again.');
    }
  };

  return (
    <div className="flex h-full">
        {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 p-4">
        <h2 className="text-lg font-semibold mb-4">Settings</h2>
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                <Icon className="w-5 h-5 mr-3" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
              {renderContent()}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteUser}
        />
      )}
    </div>
  );
}